import angular from 'angular';
import { NsjRoutingType } from '@core/nsj-core';
import {
    IMovimentacaoPeriodo,
    IMovimentacaoPeriodoCreateResponse,
    IMovimentacaoPeriodoHistoricoDetalhe,
    IMovimentacaoPeriodoHistoricoListQuery,
    IMovimentacaoPeriodoHistoricoResumo,
    IMovimentacaoPeriodoListQuery,
    IMovimentacaoPeriodoPayload,
    IMovimentacaoPeriodoUpdatePayload
} from './models/movimentacoes-periodos.service.model';

export class MovimentacoesPeriodosService {
    static $inject = ['$rootScope', '$http', '$q', 'nsjRouting'];

    constructor(
        public $rootScope: angular.IRootScopeService,
        public $http: angular.IHttpService,
        public $q: angular.IQService,
        public nsjRouting: NsjRoutingType,
    ) {
    }

    public list(query: IMovimentacaoPeriodoListQuery): angular.IPromise<IMovimentacaoPeriodo[]> {
        if (!query || !this.isNonEmptyString(query.estabelecimento)) {
            return this.rejectValidation('O campo estabelecimento deve ser informado.');
        }

        if (!this.isMeaningfulValue(query.ano)) {
            return this.rejectValidation('O campo ano deve ser informado.');
        }

        return this.$q<IMovimentacaoPeriodo[]>((resolve, reject) => {
            this.$http
                .get<IMovimentacaoPeriodo[]>(
                    this.nsjRouting.generate('movimentacoes_periodos_index', this.buildListQueryParams(query), true)
                )
                .then((response) => {
                    this.$rootScope.$broadcast('movimentacoes_periodos_index_loaded', response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast('movimentacoes_periodos_index_error', error);
                    reject(error);
                });
        });
    }

    public get(identifier: string): angular.IPromise<IMovimentacaoPeriodo> {
        if (!this.isNonEmptyString(identifier)) {
            return this.rejectValidation('O identificador do período deve ser informado.');
        }

        return this.$q<IMovimentacaoPeriodo>((resolve, reject) => {
            this.$http
                .get<IMovimentacaoPeriodo>(this.nsjRouting.generate('movimentacoes_periodos_get', { id: identifier }, true))
                .then((response) => {
                    this.$rootScope.$broadcast('movimentacoes_periodos_get_loaded', response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast('movimentacoes_periodos_get_error', error);
                    reject(error);
                });
        });
    }

    public create(payload: IMovimentacaoPeriodoPayload): angular.IPromise<IMovimentacaoPeriodoCreateResponse> {
        if (!payload || !this.isNonEmptyString(payload.estabelecimento)) {
            return this.rejectValidation('O campo estabelecimento deve ser informado.');
        }

        return this.$q<IMovimentacaoPeriodoCreateResponse>((resolve, reject) => {
            this.$http
                .post<IMovimentacaoPeriodoCreateResponse>(
                    this.nsjRouting.generate('movimentacoes_periodos_create', {}, true),
                    payload
                )
                .then((response) => {
                    this.$rootScope.$broadcast('movimentacoes_periodos_create_loaded', response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast('movimentacoes_periodos_create_error', error);
                    reject(error);
                });
        });
    }

    public update(identifier: string, payload: IMovimentacaoPeriodoUpdatePayload): angular.IPromise<void> {
        if (!this.isNonEmptyString(identifier)) {
            return this.rejectValidation('O identificador do período deve ser informado.');
        }

        return this.$q<void>((resolve, reject) => {
            this.$http
                .put<void>(this.nsjRouting.generate('movimentacoes_periodos_put', { id: identifier }, true), payload)
                .then((response) => {
                    this.$rootScope.$broadcast('movimentacoes_periodos_put_loaded', response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast('movimentacoes_periodos_put_error', error);
                    reject(error);
                });
        });
    }

    public listHistoricos(
        movimentacaoPeriodo: string,
        query: IMovimentacaoPeriodoHistoricoListQuery = {}
    ): angular.IPromise<IMovimentacaoPeriodoHistoricoResumo[]> {
        if (!this.isNonEmptyString(movimentacaoPeriodo)) {
            return this.rejectValidation('O identificador do período deve ser informado.');
        }

        return this.$q<IMovimentacaoPeriodoHistoricoResumo[]>((resolve, reject) => {
            this.$http
                .get<IMovimentacaoPeriodoHistoricoResumo[]>(
                    this.nsjRouting.generate('movimentacoes_periodos_historicos', {
                        id: movimentacaoPeriodo,
                        ...this.buildHistoricoQueryParams(query)
                    }, true)
                )
                .then((response) => {
                    this.$rootScope.$broadcast('movimentacoes_periodos_historicos_loaded', response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast('movimentacoes_periodos_historicos_error', error);
                    reject(error);
                });
        });
    }

    public getHistorico(
        movimentacaoPeriodo: string,
        historico: string
    ): angular.IPromise<IMovimentacaoPeriodoHistoricoDetalhe> {
        if (!this.isNonEmptyString(movimentacaoPeriodo)) {
            return this.rejectValidation('O identificador do período deve ser informado.');
        }

        if (!this.isNonEmptyString(historico)) {
            return this.rejectValidation('O identificador do histórico deve ser informado.');
        }

        return this.$q<IMovimentacaoPeriodoHistoricoDetalhe>((resolve, reject) => {
            this.$http
                .get<IMovimentacaoPeriodoHistoricoDetalhe>(
                    this.nsjRouting.generate('movimentacoes_periodos_historico', {
                        id: movimentacaoPeriodo,
                        historico
                    }, true)
                )
                .then((response) => {
                    this.$rootScope.$broadcast('movimentacoes_periodos_historico_loaded', response.data);
                    resolve(response.data);
                })
                .catch((error) => {
                    this.$rootScope.$broadcast('movimentacoes_periodos_historico_error', error);
                    reject(error);
                });
        });
    }

    private buildListQueryParams(query: IMovimentacaoPeriodoListQuery): IMovimentacaoPeriodoListQuery {
        const params: IMovimentacaoPeriodoListQuery = {
            estabelecimento: query.estabelecimento,
            ano: query.ano,
            order: query.order && Object.keys(query.order).length
                ? query.order
                : { mes: 'DESC' }
        };

        if (this.isMeaningfulValue(query.mes)) {
            params.mes = query.mes;
        }

        if (this.isMeaningfulValue(query.situacao)) {
            params.situacao = query.situacao;
        }

        return params;
    }

    private buildHistoricoQueryParams(query: IMovimentacaoPeriodoHistoricoListQuery): IMovimentacaoPeriodoHistoricoListQuery {
        const params: IMovimentacaoPeriodoHistoricoListQuery = {
            order: query.order && Object.keys(query.order).length
                ? query.order
                : { ordem: 'ASC' }
        };

        if (this.isMeaningfulValue(query.tipo)) {
            params.tipo = query.tipo;
        }

        if (query.offset && Object.keys(query.offset).length) {
            params.offset = query.offset;
        }

        return params;
    }

    private rejectValidation<T>(message: string): angular.IPromise<T> {
        return this.$q.reject({ message });
    }

    private isNonEmptyString(value: unknown): value is string {
        return typeof value === 'string' && value.trim().length > 0;
    }

    private isMeaningfulValue(value: unknown): boolean {
        if (value === null || typeof value === 'undefined') {
            return false;
        }

        if (typeof value === 'string') {
            return value.trim().length > 0;
        }

        return true;
    }
}
